import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import './AdminBrandAuditComponent.css';

const API_BASE_URL = 'https://api.skepper.in';
const PAGE_SIZE = 10;
const SHOW_LEAD_DETAILS = false;
const SHOW_LEAD_FILTERS = false;

const formatDate = (value, includeTime = false) => {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

const getScoreMeta = (average) => {
  const score = Number(average);
  if (!Number.isFinite(score)) return { label: 'Not available', tone: 'neutral' };
  if (score >= 4.5) return { label: 'Excellent', tone: 'excellent' };
  if (score >= 3.5) return { label: 'Good', tone: 'good' };
  if (score >= 2.5) return { label: 'Average', tone: 'average' };
  return { label: score >= 1.5 ? 'Poor' : 'Very poor', tone: 'poor' };
};

const getPdfUrl = (pdf) => {
  if (!pdf) return '';
  if (/^https?:\/\//i.test(pdf)) return pdf;
  return `${API_BASE_URL}/pdf-files/${encodeURIComponent(pdf)}`;
};

function AdminBrandAuditComponent() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchField, setSearchField] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [scoreDetails, setScoreDetails] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/get_brandaudit.php`, {
        withCredentials: true,
      });
      const data = response.data;

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to fetch assessment leads.');
      }

      setLeads(Array.isArray(data.subscribers) ? data.subscribers : []);
    } catch (error) {
      console.error('Unable to fetch assessment leads:', error);
      toast.error(error.response?.data?.message || error.message || 'Unable to load assessment leads.');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchField]);

  const sortedLeads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = query
      ? leads.filter((lead) => (searchField === 'all' ? [
          lead.id,
          lead.name,
          lead.designation,
          lead.email,
          lead.phone_number,
          lead.company_name,
          formatDate(lead.created_at),
        ] : [lead[searchField]]).some((value) => String(value || '').toLowerCase().includes(query)))
      : leads;

    return [...filtered].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [leads, searchTerm, searchField]);

  const totalPages = Math.max(1, Math.ceil(sortedLeads.length / PAGE_SIZE));
  const visibleLeads = sortedLeads.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const monthLeadCount = useMemo(() => {
    const now = new Date();
    return leads.filter((lead) => {
      const createdAt = new Date(lead.created_at);
      return createdAt.getFullYear() === now.getFullYear() && createdAt.getMonth() === now.getMonth();
    }).length;
  }, [leads]);

  const openLeadDetails = async (lead) => {
    setSelectedLead(lead);
    setScoreDetails(null);
    setDetailLoading(true);

    try {
      const body = new URLSearchParams();
      body.append('brand_audit_id', lead.id);
      const response = await axios.post(`${API_BASE_URL}/get-brand-result.php`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      if (response.data?.success && response.data.totalaverage !== undefined) {
        setScoreDetails({ average: Number(response.data.totalaverage) });
      }
    } catch (error) {
      console.error('Unable to load assessment score:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const latestSubmission = leads.length
    ? [...leads].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0]?.created_at
    : null;
  const pdfUrl = getPdfUrl(selectedLead?.pdf);
  const scoreMeta = getScoreMeta(scoreDetails?.average);

  return (
    <div className="brand-leads-page container-fluid">
      <div className="brand-leads-header">
        <div>
          <p className="brand-leads-eyebrow">Quick Brand Assessment</p>
          <h1>Assessment Leads</h1>
        </div>
      </div>

      <section className="brand-leads-summary" aria-label="Lead summary">
        <div><span>Total leads</span><strong>{leads.length}</strong></div>
        <div><span>This month</span><strong>{monthLeadCount}</strong></div>
        <div><span>Latest submission</span><strong className="brand-leads-summary-date">{formatDate(latestSubmission)}</strong></div>
      </section>

      <section className="brand-leads-workspace">
        <div className="brand-leads-toolbar">
          <div className="brand-leads-search">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search name, email, phone or company"
              aria-label="Search assessment leads"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} title="Clear search" aria-label="Clear search">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="brand-leads-toolbar-meta">
            <span className="brand-leads-count">{sortedLeads.length} {sortedLeads.length === 1 ? 'lead' : 'leads'}</span>
            {SHOW_LEAD_FILTERS && <button className="brand-leads-filter" type="button" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen} aria-controls="brand-leads-filters" title="Filter leads">
              <i className="bi bi-funnel" aria-hidden="true" />
              <span>Filter{searchField !== 'all' ? ' (active)' : ''}</span>
            </button>}
          </div>
        </div>

        {SHOW_LEAD_FILTERS && filtersOpen && (
          <div className="brand-leads-filters" id="brand-leads-filters">
            <label>Search in
              <select value={searchField} onChange={(event) => setSearchField(event.target.value)}>
                <option value="all">All fields</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="phone_number">Phone</option>
                <option value="company_name">Company</option>
                <option value="designation">Designation</option>
              </select>
            </label>
            <button type="button" className="brand-leads-filter" onClick={() => {
              setSearchTerm('');
              setSearchField('all');
            }}>Clear filters</button>
          </div>
        )}

        <div className="brand-leads-table-wrap">
          <table className="brand-leads-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Email</th>
                <th>Designation</th>
                <th>Phone Number</th>
                <th>Submitted</th>
                {SHOW_LEAD_DETAILS && <th><span className="visually-hidden">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5"><div className="brand-leads-state"><span className="brand-leads-loader" />Loading assessment leads...</div></td></tr>
              ) : visibleLeads.length ? (
                visibleLeads.map((lead, index) => (
                    <tr key={lead.id || `${lead.email}-${index}`}>
                      <td>
                        <div className="brand-lead-identity">
                          <strong>{lead.name || 'Unnamed lead'}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="brand-lead-contact">
                          <a href={`mailto:${lead.email}`}>{lead.email || 'No email'}</a>
                        </div>
                      </td>
                      <td>{lead.designation || 'Not provided'}</td>
                      <td>
                        <div className="brand-lead-contact">
                          <a href={`tel:${lead.phone_number}`}>{lead.phone_number || 'No phone'}</a>
                        </div>
                      </td>
                      <td><time dateTime={String(lead.created_at || '').slice(0, 10)}>{formatDate(lead.created_at)}</time></td>
                      {SHOW_LEAD_DETAILS && (
                        <td>
                          <button className="brand-leads-view" type="button" onClick={() => openLeadDetails(lead)} title="View full lead details" aria-label={`View details for ${lead.name || 'lead'}`}>
                            <i className="bi bi-eye" aria-hidden="true" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
              ) : (
                <tr><td colSpan="5"><div className="brand-leads-state"><i className="bi bi-inbox" aria-hidden="true" />No assessment leads match your search.</div></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && sortedLeads.length > PAGE_SIZE && (
          <footer className="brand-leads-pagination">
            <span>Page {currentPage} of {totalPages}</span>
            <div>
              <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} title="Previous page"><i className="bi bi-chevron-left" aria-hidden="true" /></button>
              <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} title="Next page"><i className="bi bi-chevron-right" aria-hidden="true" /></button>
            </div>
          </footer>
        )}
      </section>

      <Modal show={Boolean(selectedLead)} onHide={() => setSelectedLead(null)} centered className="brand-lead-detail-modal">
        <Modal.Header closeButton>
          <div>
            <p>Assessment lead</p>
            <Modal.Title>{selectedLead?.name || 'Lead details'}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="brand-lead-detail-score">
            <div>
              <span>Brand health score</span>
              {detailLoading ? <strong>Loading...</strong> : <strong>{scoreDetails ? Number(scoreDetails.average).toFixed(2) : 'Not available'}</strong>}
            </div>
            <span className={`brand-score-status is-${scoreMeta.tone}`}>{scoreMeta.label}</span>
          </div>

          <dl className="brand-lead-detail-list">
            <div><dt>Lead ID</dt><dd>{selectedLead?.id || 'Not available'}</dd></div>
            <div><dt>Name</dt><dd>{selectedLead?.name || 'Not provided'}</dd></div>
            <div><dt>Designation</dt><dd>{selectedLead?.designation || 'Not provided'}</dd></div>
            <div><dt>Company</dt><dd>{selectedLead?.company_name || 'Not provided by API'}</dd></div>
            <div><dt>Email</dt><dd>{selectedLead?.email ? <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a> : 'Not provided'}</dd></div>
            <div><dt>Phone</dt><dd>{selectedLead?.phone_number ? <a href={`tel:${selectedLead.phone_number}`}>{selectedLead.phone_number}</a> : 'Not provided'}</dd></div>
            <div><dt>Submitted</dt><dd>{formatDate(selectedLead?.created_at)}</dd></div>
            <div><dt>PDF report</dt><dd>{pdfUrl ? <a href={pdfUrl} target="_blank" rel="noopener noreferrer">Open report <i className="bi bi-box-arrow-up-right" aria-hidden="true" /></a> : 'Not generated or not provided by API'}</dd></div>
          </dl>
        </Modal.Body>
        <Modal.Footer>
          <a className="brand-lead-contact-button" href={selectedLead?.email ? `mailto:${selectedLead.email}` : undefined} aria-disabled={!selectedLead?.email}>
            <i className="bi bi-envelope" aria-hidden="true" />Email lead
          </a>
          <button type="button" className="brand-lead-close-button" onClick={() => setSelectedLead(null)}>Close</button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3500} />
    </div>
  );
}

export default AdminBrandAuditComponent;
